import React, { useState, useEffect } from 'react';
import axios from 'axios';
import contactService from '../../services/contactService';

export default function InvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [addingContact, setAddingContact] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await axios.get('/admin/invoices', { params });
      setInvoices(response.data.invoices || []);
    } catch (err) {
      console.error('Erreur lors du chargement des factures:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatAmount = (amount) => {
    return (amount / 100).toFixed(2);
  };

  const handleAddToContacts = async (invoice) => {
    setAddingContact(invoice.id);
    try {
      // Use explicit fields from backend if available, otherwise parse
      let customerName = invoice.customerName;
      let customerEmail = invoice.customerEmail;
      const customerPhone = invoice.customerPhone;

      if (!customerName || !customerEmail) {
        const [parsedName, parsedEmail] = parseCustomerInfo(invoice);
        if (!customerName) customerName = parsedName;
        if (!customerEmail) customerEmail = parsedEmail;
      }

      const contactData = {
        name: customerName,
        email: customerEmail,
        phone: customerPhone,
        type: 'individual',
        category: 'client',
        is_active: true,
        stripe_customer_id: invoice.customerId,
      };

      await contactService.createContact(contactData);
      setSuccessMessage(`✅ ${customerName} ajouté aux contacts !`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(`Erreur lors de l'ajout du contact: ${err.message}`);
    } finally {
      setAddingContact(null);
    }
  };

  const parseCustomerInfo = (invoice) => {
    const customerString = invoice.customer || '';
    const emailRegex = /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;

    let email = '';
    let name = customerString.trim();

    // Try to extract email from customer string first
    const emailMatchInCustomerString = customerString.match(/<(.+?)>/);
    if (emailMatchInCustomerString && emailRegex.test(emailMatchInCustomerString[1])) {
      email = emailMatchInCustomerString[1];
      name = customerString.replace(/<.+?>/, '').trim();
    } else {
      // If not found in <email> format, try to find a standalone email in the string
      const standaloneEmailMatch = customerString.match(emailRegex);
      if (standaloneEmailMatch) {
        email = standaloneEmailMatch[0];
        name = customerString.replace(email, '').trim();
      }
    }

    // Fallback to invoice.customer_email if available and valid
    if (!email && invoice.customer_email && emailRegex.test(invoice.customer_email)) {
      email = invoice.customer_email;
    }

    // If no valid email is found, set it to null
    if (!email || !emailRegex.test(email)) {
      email = null;
    }

    // If name is empty after email extraction, use the original customer string
    if (!name) {
      name = customerString;
    }

    return [name, email];
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
      open: { label: 'Ouvert', color: 'bg-blue-100 text-blue-800' },
      paid: { label: 'Payé', color: 'bg-green-100 text-green-800' },
      void: { label: 'Annulé', color: 'bg-red-100 text-red-800' },
      uncollectible: { label: 'Irrécupérable', color: 'bg-orange-100 text-orange-800' },
    };

    const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
      </div>
    );
  }

  if (error && !successMessage) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
          <p className="mb-4 text-red-700">Erreur: {error}</p>
          <button
            onClick={fetchInvoices}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {successMessage && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-700 animate-fade-in">
          {successMessage}
        </div>
      )}

      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <h2 className="text-xl font-bold text-gray-900">Liste des Factures</h2>
        <div className="flex items-center gap-2">
          <label htmlFor="status-filter" className="text-sm font-medium text-gray-700">Filtrer par statut:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="open">Ouvert</option>
            <option value="paid">Payé</option>
            <option value="void">Annulé</option>
            <option value="uncollectible">Irrécupérable</option>
          </select>
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl bg-white p-12 text-center shadow-sm">
          <p className="text-lg text-gray-500">Aucune facture trouvée.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Numéro</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Client</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Montant</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Payé</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Statut</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{invoice.number}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{invoice.customer}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {formatAmount(invoice.amount)} {invoice.currency.toUpperCase()}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {formatAmount(invoice.amountPaid)} {invoice.currency.toUpperCase()}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{formatDate(invoice.created)}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleAddToContacts(invoice)}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                          title="Ajouter le client aux contacts"
                          disabled={addingContact === invoice.id}
                        >
                          {addingContact === invoice.id ? '⏳' : '👤+'}
                        </button>
                        {invoice.pdfUrl && (
                          <a
                            href={invoice.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-900"
                            title="Télécharger PDF"
                          >
                            📄
                          </a>
                        )}
                        {invoice.hostedInvoiceUrl && (
                          <a
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-900"
                            title="Voir la facture"
                          >
                            👁️
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * TODO: After DB integration
 * - Check if contact already exists before adding (avoid duplicates)
 * - Link the invoice to the contact (contact_id in invoices table)
 * - Show "Contact already exists" message if duplicate
 * - Add a "Remove from contacts" option
 */
